import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, Link } from "react-router-dom";
import axios from "axios";

// Pages
import AdminLoginPage from "./pages/AdminLoginPage";
import AdminDashboard from "./pages/GatewayAdminDashboard";
import PublicCheckoutPage from "./pages/PublicCheckoutPage";
import ReceiptReaderPage from "./pages/ReceiptReaderPage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const isReceiptReaderHost = () => {
  const host = window.location.hostname.toLowerCase();
  return host.startsWith("receipt") || host.startsWith("receipts") || host.includes("nip.io");
};

const Home = () => {
  useEffect(() => {
    // Quietly verify backend is reachable; ignore failures (public endpoint).
    axios.get(`${API}/`).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 relative overflow-hidden">
      {/* Subtle ambient glow */}
      <div aria-hidden className="pointer-events-none absolute inset-0 opacity-60">
        <div className="absolute -top-24 -left-24 w-[480px] h-[480px] rounded-full bg-emerald-500/10 blur-3xl" />
        <div className="absolute top-1/3 -right-32 w-[420px] h-[420px] rounded-full bg-blue-500/10 blur-3xl" />
      </div>

      {/* Top bar */}
      <header className="relative z-10 border-b border-zinc-900/80 backdrop-blur supports-[backdrop-filter]:bg-zinc-950/40">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-emerald-500/10 ring-1 ring-emerald-500/30 flex items-center justify-center">
              <svg className="w-5 h-5 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            </div>
            <div className="leading-tight">
              <p className="text-sm font-semibold text-white">Gateway Console</p>
              <p className="text-[11px] text-zinc-500">Multi-client payment orchestration</p>
            </div>
          </div>
          <Link
            to="/admin/login"
            data-testid="home-admin-login-link"
            className="px-4 py-2 text-sm font-medium text-zinc-200 bg-zinc-900 ring-1 ring-zinc-800 hover:ring-zinc-700 hover:bg-zinc-800 rounded-lg transition"
          >
            Admin Login
          </Link>
        </div>
      </header>

      {/* Hero */}
      <main className="relative z-10 max-w-6xl mx-auto px-6 pt-20 pb-24">
        <div className="grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-7">
            <span className="inline-flex items-center gap-2 text-[11px] font-medium uppercase tracking-wide text-emerald-300 bg-emerald-500/10 ring-1 ring-emerald-500/30 px-2.5 py-1 rounded-full">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              Gateway is online
            </span>
            <h1 className="mt-5 text-4xl sm:text-5xl font-bold tracking-tight text-white leading-[1.05]">
              One gateway.<br />
              <span className="text-emerald-400">Many clients.</span>
            </h1>
            <p className="mt-5 text-zinc-400 max-w-xl text-[15px] leading-relaxed">
              Issue per-client API keys, sign outbound webhooks, and route payments
              through CashApp, Chime, Crypto, Apple/Google Pay, escrow, and manual
              receipt verification — all from one console.
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link
                to="/admin/login"
                data-testid="home-cta-primary"
                className="inline-flex items-center gap-2 px-5 py-3 bg-emerald-500 hover:bg-emerald-400 text-zinc-950 font-semibold rounded-lg transition shadow-lg shadow-emerald-500/20"
              >
                Open admin console
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </Link>
              <a
                href="#how"
                className="inline-flex items-center gap-2 px-5 py-3 text-sm font-medium text-zinc-300 hover:text-white transition"
              >
                How it works
              </a>
            </div>

            {/* Quick stats / chips */}
            <dl className="mt-10 grid grid-cols-3 gap-3 max-w-lg">
              {[
                { k: 'Providers', v: '7' },
                { k: 'Methods', v: '25+' },
                { k: 'Per-client keys', v: '✓' },
              ].map((s) => (
                <div key={s.k} className="rounded-xl bg-zinc-900/70 ring-1 ring-zinc-800 px-4 py-3">
                  <dd className="text-xl font-semibold text-white">{s.v}</dd>
                  <dt className="text-[11px] uppercase tracking-wide text-zinc-500 mt-0.5">{s.k}</dt>
                </div>
              ))}
            </dl>
          </div>

          {/* Code preview card */}
          <div className="lg:col-span-5">
            <div className="rounded-2xl bg-zinc-900/70 ring-1 ring-zinc-800 backdrop-blur shadow-2xl">
              <div className="flex items-center gap-1.5 px-4 h-9 border-b border-zinc-800/80">
                <span className="w-2.5 h-2.5 rounded-full bg-red-500/70" />
                <span className="w-2.5 h-2.5 rounded-full bg-amber-500/70" />
                <span className="w-2.5 h-2.5 rounded-full bg-emerald-500/70" />
                <span className="ml-3 text-[11px] text-zinc-500 font-mono">create-link.sh</span>
              </div>
              <pre className="p-5 text-[12px] leading-relaxed text-zinc-300 font-mono overflow-x-auto">
{`curl -X POST $GATEWAY/api/gateway/v1/create-link \\
  -H "x-api-key: $YOUR_CLIENT_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "depositId": "order_12345",
    "method": "manual_cashapp",
    "amount": "9.99"
  }'`}
              </pre>
              <div className="px-5 pb-5 pt-1 text-[11px] text-zinc-500">
                Issued from the <span className="text-zinc-300">Clients</span> tab. Webhook secret signs the callback.
              </div>
            </div>
          </div>
        </div>

        {/* How it works — three steps */}
        <section id="how" className="mt-24 grid sm:grid-cols-3 gap-4">
          {[
            {
              n: '01',
              title: 'Add a client',
              body: 'Each external system gets its own API key, webhook secret, and callback URL.',
            },
            {
              n: '02',
              title: 'Create a payment link',
              body: 'POST to /api/gateway/v1/create-link. We pick the right provider for the method.',
            },
            {
              n: '03',
              title: 'Receive a signed webhook',
              body: 'On terminal status we POST to your URL with HMAC-SHA256 signature headers.',
            },
          ].map((s) => (
            <div key={s.n} className="rounded-2xl bg-zinc-900/50 ring-1 ring-zinc-800 p-5">
              <p className="text-emerald-400 text-xs font-mono">{s.n}</p>
              <h3 className="mt-2 text-white font-semibold">{s.title}</h3>
              <p className="mt-1 text-sm text-zinc-400 leading-relaxed">{s.body}</p>
            </div>
          ))}
        </section>
      </main>

      <footer className="relative z-10 border-t border-zinc-900/80">
        <div className="max-w-6xl mx-auto px-6 h-14 flex items-center justify-between text-[11px] text-zinc-500">
          <p>© Gateway Console</p>
          <p className="font-mono">/api/gateway/v1</p>
        </div>
      </footer>
    </div>
  );
};

function App() {
  const receiptOnly = isReceiptReaderHost();

  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          {receiptOnly ? <Route path="/" element={<ReceiptReaderPage />} /> : null}
          {/* Home */}
          <Route path="/" element={receiptOnly ? <ReceiptReaderPage /> : <Home />} />
          
          {/* Admin Routes */}
          <Route path="/admin/login" element={<AdminLoginPage />} />
          <Route path="/admin/dashboard" element={<AdminDashboard />} />
          <Route path="/admin/mailboxes" element={<AdminDashboard />} />
          <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />
          
          {/* Public Checkout Page */}
          <Route path="/pay/:token" element={<PublicCheckoutPage />} />
          <Route path="/receipt-reader" element={<ReceiptReaderPage />} />
          
          {/* Catch all */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
